import React, { useState, useRef } from 'react';
import { Select, Button, Input, Space, Dropdown, Menu } from 'antd';
import {
  FilterOutlined,
  ReloadOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  DownloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import DateRangeSelector from '../../DateRangeSelector';

const { Option } = Select;
const { Search } = Input;

/**
 * Component that handles all filtering, sorting and search functionality for orders
 */
const OrderFilters = ({
  dateViewMode,
  currentDate,
  dateRange,
  dateDisplayText,
  isCustomRange,
  orderStatusFilter,
  paymentStatusFilter,
  searchText,
  searchCategory,
  sortInfo,
  onDateRangeChange,
  onCustomRangeChange,
  onFilterChange,
  onResetFilters,
  onSearch,
  onSearchCategoryChange,
  onSortChange,
  onExport,
  onExportCSV,
  onRefresh,
  loading,
  isAdmin = false
}) => {
  const searchInputRef = useRef(null);
  
  // Sort menu items for dropdown
  const sortMenuItems = [
    {
      key: 'property_date_desc',
      label: (
        <div className="flex items-center">
          <span>Date (newest first)</span>
          {sortInfo.columnKey === 'property_date' && sortInfo.order === 'descend' && (
            <SortDescendingOutlined className="ml-2" />
          )}
        </div>
      ),
      onClick: () => onSortChange('property_date', 'descend')
    },
    {
      key: 'property_date_asc',
      label: (
        <div className="flex items-center">
          <span>Date (oldest first)</span>
          {sortInfo.columnKey === 'property_date' && sortInfo.order === 'ascend' && (
            <SortAscendingOutlined className="ml-2" />
          )}
        </div>
      ),
      onClick: () => onSortChange('property_date', 'ascend')
    },
    {
      key: 'id_desc',
      label: (
        <div className="flex items-center">
          <span>Order ID (newest first)</span>
          {sortInfo.columnKey === 'id' && sortInfo.order === 'descend' && (
            <SortDescendingOutlined className="ml-2" />
          )}
        </div>
      ),
      onClick: () => onSortChange('id', 'descend')
    },
    {
      key: 'id_asc',
      label: (
        <div className="flex items-center">
          <span>Order ID (oldest first)</span>
          {sortInfo.columnKey === 'id' && sortInfo.order === 'ascend' && (
            <SortAscendingOutlined className="ml-2" />
          )}
        </div>
      ),
      onClick: () => onSortChange('id', 'ascend')
    },
    {
      key: 'order_total_desc',
      label: (
        <div className="flex items-center">
          <span>Total (highest first)</span>
          {sortInfo.columnKey === 'order_total' && sortInfo.order === 'descend' && (
            <SortDescendingOutlined className="ml-2" />
          )}
        </div>
      ),
      onClick: () => onSortChange('order_total', 'descend')
    },
    {
      key: 'order_total_asc',
      label: (
        <div className="flex items-center">
          <span>Total (lowest first)</span>
          {sortInfo.columnKey === 'order_total' && sortInfo.order === 'ascend' && (
            <SortAscendingOutlined className="ml-2" />
          )}
        </div>
      ),
      onClick: () => onSortChange('order_total', 'ascend')
    }
  ];
  
  // Sort menu dropdown
  const sortMenu = (
    <Menu items={sortMenuItems} />
  );
  
  // Search categories dropdown
  const searchCategories = [
    { value: 'all', label: 'All Fields' },
    { value: 'property', label: 'Property Name' },
    { value: 'address', label: 'Address' },
    { value: 'postcode', label: 'Post Code' }
  ];
  
  // Add Customer search for admin users
  if (isAdmin) {
    searchCategories.push({ value: 'customer', label: 'Customer' });
  }
  
  return (
    <div className="order-filters">
      {/* Date Navigation and Filter Controls */}
      <div className="flex flex-col gap-4 xl:flex-row xl:justify-between xl:items-center mb-4">
        <div className="w-full xl:w-auto">
          <DateRangeSelector
            dateViewMode={dateViewMode}
            currentDate={currentDate}
            dateRange={dateRange}
            dateDisplayText={dateDisplayText}
            isCustomRange={isCustomRange}
            onDateRangeChange={onDateRangeChange}
            onCustomRangeChange={onCustomRangeChange}
          />
        </div>

        <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center xl:justify-end">
          {/* Enhanced Search */}
          <div className="flex w-full sm:w-[360px]">
            <Select 
              value={searchCategory}
              onChange={onSearchCategoryChange}
              style={{ width: 130 }}
              className="rounded-r-none"
            >
              {searchCategories.map(cat => (
                <Option key={cat.value} value={cat.value}>{cat.label}</Option>
              ))}
            </Select>
            
            <Search
              placeholder="Search orders..."
              allowClear
              style={{ width: '100%' }}
              value={searchText}
              onChange={e => onSearch(e.target.value)}
              onSearch={value => onSearch(value)}
              ref={searchInputRef}
              className="rounded-l-none"
            />
          </div>

          {/* Status Filter */}
          <Select
            placeholder="Order Status"
            allowClear
            className="w-full sm:w-40"
            onChange={value => onFilterChange('order_status', value)}
            value={orderStatusFilter}
          >
            <Option value="Scheduled">Scheduled</Option>
            <Option value="En-route">En-Route</Option>
            <Option value="Completed">Completed</Option>
            <Option value="Collected">Collected</Option>
            <Option value="Cancelled">Cancelled</Option>
            <Option value="Failed">Failed</Option>
            {isAdmin && <Option value="not_scheduled">Not Scheduled</Option>}
          </Select>

          {/* Payment Status Filter (admin only) */}
          {isAdmin && (
            <Select
              placeholder="Payment Status"
              allowClear
              className="w-full sm:w-40"
              onChange={value => onFilterChange('payment_status', value)}
              value={paymentStatusFilter}
            >
              <Option value="1">Paid</Option>
              <Option value="0">Unpaid</Option>
            </Select>
          )}

          {/* Sort Dropdown */}
          <Dropdown overlay={sortMenu} trigger={['click']}>
            <Button icon={<FilterOutlined />} className="w-full sm:w-auto">
              Sort
            </Button>
          </Dropdown>

          {/* Reset Button */}
          <Button
            icon={<ReloadOutlined />}
            onClick={onResetFilters}
            className="w-full sm:w-auto flex items-center justify-center"
          >
            Reset
          </Button>

          {/* Export Button */}
          <Button
            icon={<DownloadOutlined />}
            onClick={onExport}
            className="w-full sm:w-auto flex items-center justify-center"
          >
            Export
          </Button>

          <Button
            icon={<DownloadOutlined />}
            onClick={onExportCSV}
            className="w-full sm:w-auto flex items-center justify-center"
          >
            Export CSV
          </Button>

          {/* Refresh Button */}
          <Button
            type="primary"
            onClick={onRefresh}
            icon={<ReloadOutlined />}
            loading={loading}
            className="bg-golden-navy w-full sm:w-auto"
          >
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderFilters;
